import React, { useState } from "react";
import { ArrowLeft, Send, Loader2, HelpCircle, AlertTriangle, CreditCard, Lightbulb, UserCog, MessageSquare, CheckCircle2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  { key: "Bug Report", icon: AlertTriangle, color: "text-red-500 bg-red-50 border-red-200", desc: "Something isn't working right" },
  { key: "Billing Issue", icon: CreditCard, color: "text-amber-600 bg-amber-50 border-amber-200", desc: "Payment or subscription problems" },
  { key: "Feature Request", icon: Lightbulb, color: "text-violet-500 bg-violet-50 border-violet-200", desc: "Suggest an improvement" },
  { key: "Account Issue", icon: UserCog, color: "text-blue-500 bg-blue-50 border-blue-200", desc: "Login, profile, or data issues" },
  { key: "General Question", icon: MessageSquare, color: "text-gray-600 bg-gray-50 border-gray-200", desc: "Anything else" },
];

const SupportPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [category, setCategory] = useState(null);
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [ticketId, setTicketId] = useState("");

  const handleSubmit = async () => {
    if (!message.trim() || !category) return;
    setSubmitting(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/support/tickets`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: user?.name || "", email: user?.email || "", category, message }),
      });
      if (res.ok) {
        const data = await res.json();
        setTicketId(data.ticket_id);
        setSubmitted(true);
      }
    } catch (e) { /* ignore */ }
    setSubmitting(false);
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gray-50 px-4 py-6">
        <div className="max-w-lg mx-auto text-center space-y-6 pt-12">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
            <CheckCircle2 className="h-8 w-8 text-green-600" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-900">Ticket Submitted</h2>
            <p className="text-sm text-gray-500 mt-2">We've received your {category?.toLowerCase()} and will get back to you soon.</p>
          </div>
          <Card className="bg-gray-50 border-gray-200">
            <CardContent className="p-4">
              <p className="text-xs text-gray-500">Ticket ID</p>
              <p className="text-sm font-mono font-medium text-gray-800" data-testid="ticket-id">{ticketId}</p>
            </CardContent>
          </Card>
          <Button
            onClick={() => navigate("/")}
            className="bg-gradient-to-r from-green-500 to-emerald-500 text-white"
            data-testid="back-home-btn"
          >
            Back to Home
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 px-4 py-4">
      <div className="max-w-lg mx-auto space-y-5">
        {/* Header */}
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="support-back-btn">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-lg font-bold text-gray-900">Help & Support</h1>
            <p className="text-xs text-gray-500">We're here to help</p>
          </div>
        </div>

        {/* Category Selection */}
        {!category ? (
          <div className="space-y-3">
            <p className="text-sm font-medium text-gray-700">What do you need help with?</p>
            <div className="space-y-2">
              {CATEGORIES.map(({ key, icon: Icon, color, desc }) => (
                <button
                  key={key}
                  onClick={() => setCategory(key)}
                  className={`w-full flex items-center gap-3 p-3.5 rounded-xl border transition-all hover:shadow-sm ${color}`}
                  data-testid={`cat-${key.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-white/60">
                    <Icon className="h-5 w-5" />
                  </div>
                  <div className="text-left flex-1">
                    <p className="text-sm font-semibold text-gray-800">{key}</p>
                    <p className="text-xs text-gray-500">{desc}</p>
                  </div>
                </button>
              ))}
            </div>
          </div>
        ) : (
          /* Ticket Form */
          <div className="space-y-4">
            <button
              onClick={() => setCategory(null)}
              className="flex items-center gap-2 text-sm text-violet-600 hover:text-violet-700"
              data-testid="change-category-btn"
            >
              <HelpCircle className="h-4 w-4" />
              {category} — change
            </button>

            <div className="space-y-3">
              <div>
                <label className="text-xs font-medium text-gray-600 mb-1 block">Describe your issue</label>
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Tell us what's happening..."
                  rows={5}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-400 focus:border-transparent resize-none"
                  data-testid="support-message"
                />
              </div>
            </div>

            <Button
              onClick={handleSubmit}
              disabled={!message.trim() || submitting}
              className="w-full bg-gradient-to-r from-violet-500 to-fuchsia-500 text-white"
              data-testid="submit-ticket-btn"
            >
              {submitting ? (
                <><Loader2 className="h-4 w-4 animate-spin mr-2" />Sending...</>
              ) : (
                <><Send className="h-4 w-4 mr-2" />Submit Ticket</>
              )}
            </Button>

            <p className="text-xs text-gray-400 text-center">
              We'll respond within 24 hours.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default SupportPage;
